// TODO: provide types instead of any everywhere
/* eslint-disable @typescript-eslint/no-explicit-any */
import classNames from 'clsx';
import produce from 'immer';
import { get, isEmpty, isNumber, set, throttle } from 'lodash';
import React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import CasinoGameHeader from '@swf/components/CasinoGameHeader/CasinoGameHeader';
import CasinoGameIcon from '@swf/components/CasinoGameIcon/CasinoGameIcon';
import CasinoGameList from '@swf/components/CasinoGameList/CasinoGameList';
import NoGamesFound from '@swf/components/NoGamesFound/NoGamesFound';
import { Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import type { SliderHandles, State as SliderState } from '@swf/components/Slider/Slider';
import { Header } from '@swf/core/enums';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { CasinoGame, Page } from '@swf/core/types/CasinoGame';
import type { FetchServiceMeta, WidgetInstanceProps, WidgetReducers } from '@swf/core/types/Widget';
import { WidgetComponent } from '@swf/core/types/Widget';
import type { Filter } from '@swf/core/utils/casinoFilters';
import { Service, menus } from '@swf/core/utils/casinoFilters';
import type { CasinoIcon } from '@swf/generated/types/common/Casino';
import type { CasinoServiceParameters } from '@swf/generated/types/common/CasinoParams';
import type {
  CasinoGameWidgetConfig,
  CasinoGameWidgetServices
} from '@swf/generated/types/widgets/CasinoGameWidget';

import styles from './CasinoGameWidget.scss';

export type OwnProps = WidgetInstanceProps<
  CasinoGameWidgetConfig,
  CasinoGameWidgetServices,
  Page[]
>;

const mapState = (rootState: RootState) => ({
  currency: select.currency.get(rootState),
  isAuthenticated: select.session.isAuthenticated(rootState),
  launchCount: select.casino.getLaunchCount(rootState),
  location: select.location.get(rootState),
  siteName: select.site.getName(rootState)
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;

export type Props = OwnProps & PropsFromRedux;
export type State = { active: number; loading: boolean[] } & SliderState;

interface TemplateValues {
  service: Service;
}

const gameUpdater = (data: Page[] | undefined, callback: (game: CasinoGame) => void) =>
  produce(data, (draft) => draft?.forEach((page: any) => page.games.forEach(callback)));

// TODO: Provide types or refactor without reducers
// @ts-ignore Types issues with Service Reducer.
class CasinoGameWidget extends WidgetComponent<Props, State> {
  static serviceReducers: WidgetReducers<CasinoGameWidgetConfig, Props['data']> = {
    games: (state = [], { data, config, meta = {} }) => {
      const games = data.favorites || data;
      return produce(state, (draft) => {
        const { index = 0, page = 0, size = games.length } = meta;
        if (!draft[index] || !config.paginate || page === 0) {
          draft[index] = { games, complete: !config.paginate, page };
        } else {
          draft[index].games.splice(page * size, size, ...games);
          draft[index].complete = games.length < size;
          draft[index].page = page;
        }
      });
    },
    jackpot: (state = [], { data }) =>
      gameUpdater(
        state,
        (game) => game.jackpot && game.jackpot.key === data.key && (game.jackpot.value = data.value)
      ),
    freeSpins: (state, { data }) =>
      gameUpdater(state, (game) => {
        game.fsb = false;
        return data.forEach(
          ({ gId, vId }: Record<string, string>) =>
            game.gametype === +gId && game.variant === +vId && (game.fsb = true)
        );
      }),
    favorites: (state, { data }) =>
      gameUpdater(state, (game) => {
        game.isFavorite = false;
        return data.forEach(
          ({ gametype, variant }: Record<string, string>) =>
            game.gametype === +gametype && game.variant === +variant && (game.isFavorite = true)
        );
      })
  };

  slider = React.createRef<SliderHandles>();
  container = React.createRef<HTMLDivElement>();
  state: State = {
    left: false,
    right: false,
    active: this.getDefaultFilter(),
    loading: []
  };

  handleScroll = throttle(async () => {
    if (this.props.config.paginate && this.container.current) {
      const rect = this.container.current.getBoundingClientRect() as DOMRect;
      const docElem = document.documentElement as HTMLElement;
      const bottom = Math.max(docElem.clientHeight, window.innerHeight || 0) * 1.5;
      if (rect.y + rect.height < bottom) {
        this.loadMore();
      }
    }
  }, 200);

  handleSlide = async (state: State) => {
    const { paginate } = this.props.config;
    const { complete } = this.getPage();
    if (paginate && !state.right && !complete) {
      await this.loadMore();
    }
    this.setState(state);
  };

  handleFilter = (active: number) => this.setState({ active }, this.handleScroll);

  getFilter = (index = this.state.active): Filter | undefined => {
    const menu = menus[this.props.config.filters];
    return menu ? menu[index] : undefined;
  };

  getPage = (index = this.state.active): Partial<Page> => get(this.props, ['data', index], {});

  getIcon = (): CasinoIcon => get(this.props, 'services.games.params.ims.tags[0]', 'icnSquare');

  isLoading = (index = this.state.active) => this.state.loading[index];

  buildPayload = (index: number) => {
    const filter = this.getFilter(index);
    const service = this.props.services.games;
    const templateValues = { ...service.config.templateValues } as TemplateValues;
    let params: CasinoServiceParameters = { ...(service.params as CasinoServiceParameters) };
    if (filter) {
      params = filter.makeQuery(params, this.props.siteName);
      templateValues.service = filter.service;
    }
    const meta = { templateValues, index, size: params.psz };
    return { meta, params };
  };

  currentService = () =>
    (this.props.services.games.config.templateValues as TemplateValues).service;

  fetchGames = async (index: number, params: any, meta: any) =>
    // eslint-disable-next-line no-async-promise-executor
    new Promise<void>(async (resolve) => {
      const recipe = (value: any) => (draft: any) => set(draft, index, value);
      this.setState({ loading: produce(this.state.loading, recipe(true)) });
      await this.props.fetchService('games', params, { ...meta, page: params.pg });
      this.setState({ loading: produce(this.state.loading, recipe(false)) }, resolve);
    });

  loadMore = async (index = this.state.active) => {
    const { complete, page } = this.getPage(index);
    if (!complete && !this.isLoading(index)) {
      const pg = isNumber(page) ? page + 1 : 0;
      const { meta, params } = this.buildPayload(index);
      await this.fetchGames(index, { ...params, pg }, meta);
    }
  };

  refreshPage = async (index: number) => {
    const { meta, params } = this.buildPayload(index);
    await this.fetchGames(index, params, meta);
  };

  refreshFilters = () =>
    this.getFilter() &&
    menus[this.props.config.filters].forEach(
      (item, index) => (this.props.isAuthenticated || !item.auth) && this.refreshPage(index)
    );

  refresh = (...args: Service[]) => {
    const { data = [] } = this.props;
    data.forEach((page, index) => {
      const service = get(this.getFilter(index), 'service', this.currentService());
      if (!args.length || args.includes(service)) {
        this.refreshPage(index);
      }
    });
  };

  getDefaultFilter() {
    const { filters, tab } = this.props.config;
    const index = menus[filters] && menus[filters].findIndex((item) => item.slug === tab);
    return index && index > -1 ? index : 0;
  }

  updateSubscriptions() {
    const { updateSubscriptions, data = [], config } = this.props;
    const callback = (memo: any) => (game: any) =>
      game.jackpot && (memo[game.jackpot.key] = { templateValues: game.jackpot });
    const metas = Object.values(
      data.reduce((memo, page) => {
        page.games.forEach(callback(memo));
        return memo;
      }, {})
    ) as FetchServiceMeta[];

    if (metas.length) {
      updateSubscriptions('jackpot', metas);
    }

    if (config.fsbPlaceholder && this.currentService().match(Service.FreeSpinBonus)) {
      updateSubscriptions('freeSpins', [
        {
          reducer: (state: Page[] | undefined, payload: any) => {
            this.props.fetchService('games');
            return CasinoGameWidget.serviceReducers.freeSpins(state, payload);
          }
        }
      ]);
    }
  }

  async componentDidMount() {
    const { paginate, slide } = this.props.config;

    this.refreshFilters();
    if (paginate && !slide) {
      await this.loadMore();
      window.addEventListener('scroll', this.handleScroll, true);
    }

    this.updateSubscriptions();
  }

  componentDidUpdate(lastProps: Props, lastState: State) {
    const { data, isAuthenticated, config, services, clear } = this.props;
    const filter = this.getFilter();
    const authChanged = lastProps.isAuthenticated !== isAuthenticated;
    const loggingOut = authChanged && !isAuthenticated;

    if (data !== lastProps.data) {
      this.updateSubscriptions();
    }

    if (loggingOut && filter && filter.auth) {
      this.handleFilter(0);
    }

    if (loggingOut && filter) {
      [...menus[config.filters]].reverse().forEach((item, index) => item.auth && clear(index));
    } else if (loggingOut && !services.games.config.onLoad && services.games.config.onAuth) {
      clear();
    }

    if (!authChanged && this.props.currency !== lastProps.currency) {
      this.refresh();
    } else if (isAuthenticated && lastProps.launchCount < this.props.launchCount) {
      this.refresh(Service.Recent);
    } else if (authChanged) {
      this.refreshFilters();
      this.refresh(Service.Recent, Service.Recommended);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  render() {
    const { config, style, data = [], preload, isAuthenticated } = this.props;
    const { games = [] } = this.getPage();
    const fsbWidget = !!this.currentService().match(Service.FreeSpinBonus);
    const shouldRender =
      !!(this.getFilter() || games?.length >= config.minGames) && !(fsbWidget && !isAuthenticated);

    if (!shouldRender) {
      return null;
    }

    const { active } = this.state;
    const { filters, minGames, fsbPlaceholder, mosaicLayout, ...rest } = config;
    const { title, slide } = config;
    const hasHeader = title || slide || filters !== 'none';
    const icon = this.getIcon();
    const loading = this.isLoading();
    const placeholder = fsbPlaceholder && fsbWidget ? <CasinoGameIcon FSBPlaceholder /> : undefined;
    const noGamesFound =
      !isEmpty(data) && !data.some((dataPage) => !isEmpty(get(dataPage, 'games', [])));

    return (
      <section
        className={classNames(styles.container, styles[`${icon}Container`])}
        style={style}
        ref={this.container}
      >
        <Vary by={[Header.Country, Header.Currency]} />
        {hasHeader && (
          <CasinoGameHeader
            {...config}
            slide={slide}
            data={data}
            icon={icon}
            sliderRef={this.slider}
            sliderState={this.state}
            onFilter={this.handleFilter}
            active={active}
            widgetRef={this.container}
          />
        )}
        {noGamesFound ? (
          <NoGamesFound />
        ) : (
          <CasinoGameList
            {...rest}
            preload={preload}
            games={games}
            icon={icon}
            loading={loading}
            onScroll={this.handleSlide}
            placeholder={placeholder}
            sliderRef={this.slider}
            fsbLaunch={fsbWidget}
            mosaic={mosaicLayout}
          />
        )}
      </section>
    );
  }
}

export default connector(CasinoGameWidget);
